/**
 * ListItemCard — the captured-item / capture-panel card (ported from
 * Noteble — ADR-022). Two shapes:
 * - captured item: icon tab on the left, content beside it
 * - active capture panel: content on top, icon + action row at the bottom
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { CaptureActionRow } from './CaptureActionRow';

interface ListItemCardProps {
  onClick?: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
  right?: React.ReactNode;
  selected?: boolean;
  /** Temporary highlight (e.g. just-added item) */
  highlighted?: boolean;
  className?: string;

  // Built-in action row — renders center buttons | Capture (right) with Discard
  /** Handler for the Capture button. Omit to hide the button. */
  onSend?: () => void;
  /** Handler for the Discard button. Omit to hide the entire action row. */
  onDiscard?: () => void;
  sendDisabled?: boolean;
  sendLabel?: string;
  discardLabel?: string;
  /** Extra buttons rendered between Discard and Capture (e.g. mic, pause) */
  actionChildren?: React.ReactNode;
}

export const ListItemCard: React.FC<ListItemCardProps> = ({
  onClick,
  icon,
  children,
  right,
  selected,
  highlighted,
  className,
  onSend,
  onDiscard,
  sendDisabled,
  sendLabel,
  discardLabel,
  actionChildren,
}) => {
  const Component = onClick ? 'button' : 'div';
  const hasActionRow = !!onDiscard;

  return (
    <Component
      onClick={onClick}
      className={cn(
        'w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-800 text-left transition-all duration-200 overflow-hidden',
        hasActionRow ? 'flex flex-col p-3' : 'flex',
        selected && 'border-blue-500/30 dark:border-blue-500/30',
        highlighted && 'ring-2 ring-blue-500/50 border-blue-500/40',
        onClick && 'cursor-pointer',
        className,
      )}
    >
      {/* Captured state: icon tab on the left */}
      {!hasActionRow && (
        <div className="shrink-0 flex items-center justify-center px-3 bg-slate-50/80 dark:bg-slate-800">
          {icon}
        </div>
      )}

      {/* Content */}
      <div className={cn('flex items-center gap-3', !hasActionRow && 'flex-1 min-w-0 p-3')}>
        <div className="flex-1 min-w-0">{children}</div>
        {right && <div className="shrink-0">{right}</div>}
      </div>

      {/* Active capture: icon + action row at the bottom */}
      {hasActionRow && (
        <div className="flex items-center gap-2 mt-4">
          <div className="shrink-0">{icon}</div>
          <CaptureActionRow
            onSend={onSend}
            onDiscard={onDiscard}
            sendDisabled={sendDisabled}
            sendLabel={sendLabel}
            discardLabel={discardLabel}
            className="flex-1 mt-0"
          >
            {actionChildren}
          </CaptureActionRow>
        </div>
      )}
    </Component>
  );
};
